/**
 * Phase 14.14 — Shared 3D viewer helpers.
 *
 * Single source of truth for the visual contract shared across the
 * three (now) 3D viewers in the workbench:
 *   - PkTrajectory3D (Phase 11)
 *   - GeometrySweep3D (Phase 14.13)
 *   - SensitivityDriver3D (Phase 14.14, this phase)
 *
 * Why a shared module: the dev's prior reuse decision in Phase 14.13
 * deliberately avoided extending PkTrajectory3D with a "mode" prop
 * because PK semantics force too many branches. The same logic
 * applies to sensitivity. Three sibling components each owning their
 * own scene composition, but ALL referring to a single set of
 * geometry primitives + camera defaults + colour ramp, so a UX
 * adjustment (e.g. retuning the locked camera angles) is a single
 * file edit instead of three coordinated edits.
 */

// ──────────────────────────────────────────────────────────────────
// Polar → Cartesian.
// East = range·sin(bearing), North = range·cos(bearing), Up = altitude.
// Bearing convention: 0° = North, +ve = clockwise (matches the
// engine's threat_motion + scenario builders).
// ──────────────────────────────────────────────────────────────────
export function polarToCart(rangeM, bearingDeg, altitudeM) {
    const rad = (Number(bearingDeg ?? 0) * Math.PI) / 180;
    return {
        x: Number(rangeM ?? 0) * Math.sin(rad),
        y: Number(rangeM ?? 0) * Math.cos(rad),
        z: Number(altitudeM ?? 0),
    };
}

// ──────────────────────────────────────────────────────────────────
// RdYlGn-ish ramp — three stops:
//   p=0.0 → red-400  rgb(248, 113, 113)
//   p=0.5 → amber-400 rgb(251, 191, 36)
//   p=1.0 → emerald-400 rgb(52, 211, 153)
// Closely tracks matplotlib's RdYlGn so PDF + UI read consistently.
// ──────────────────────────────────────────────────────────────────
export function pkColour(value) {
    const p = Math.max(0, Math.min(1, Number(value ?? 0)));
    const stops = [
        { p: 0.0, r: 248, g: 113, b: 113 },
        { p: 0.5, r: 251, g: 191, b: 36 },
        { p: 1.0, r: 52, g: 211, b: 153 },
    ];
    let lo = stops[0];
    let hi = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i += 1) {
        if (p >= stops[i].p && p <= stops[i + 1].p) {
            lo = stops[i];
            hi = stops[i + 1];
            break;
        }
    }
    const span = hi.p - lo.p || 1;
    const t = (p - lo.p) / span;
    const r = Math.round(lo.r + (hi.r - lo.r) * t);
    const g = Math.round(lo.g + (hi.g - lo.g) * t);
    const b = Math.round(lo.b + (hi.b - lo.b) * t);
    return `rgb(${r}, ${g}, ${b})`;
}

// ──────────────────────────────────────────────────────────────────
// Locked camera + scene defaults — applied to every 3D viewer so all
// three render the same scene from the same angle. Tuning happens
// here once.
// ──────────────────────────────────────────────────────────────────
export const LOCKED_CAMERA_EYE = { x: 1.6, y: 1.4, z: 0.9 };

export function buildSceneLayout({
    paperBg = "#0f172a",
    plotBg = "#0f172a",
    fontColour = "#cbd5e1",
    axisGrid = "#1e293b",
    axisLabelColour = "#94a3b8",
} = {}) {
    return {
        paper_bgcolor: paperBg,
        plot_bgcolor: plotBg,
        font: { color: fontColour },
        margin: { l: 0, r: 0, b: 0, t: 24 },
        showlegend: true,
        legend: { font: { color: fontColour } },
        scene: {
            xaxis: { title: "East (m)", gridcolor: axisGrid, color: axisLabelColour },
            yaxis: { title: "North (m)", gridcolor: axisGrid, color: axisLabelColour },
            zaxis: {
                title: "Altitude (m)",
                gridcolor: axisGrid,
                color: axisLabelColour,
            },
            aspectmode: "data",
            camera: { eye: LOCKED_CAMERA_EYE },
        },
    };
}

// ──────────────────────────────────────────────────────────────────
// Plotly mode-bar buttons we DON'T want in the workbench (operators
// found `lasso2d` and `select2d` distracting; `sendDataToCloud`
// sends data off-prem).
// ──────────────────────────────────────────────────────────────────
export const PLOT_BUTTONS_TO_REMOVE = [
    "sendDataToCloud",
    "lasso2d",
    "select2d",
];

// ──────────────────────────────────────────────────────────────────
// Phase 17.8 follow-up — Item 2. Vehicle markers contract.
//
// Operators reported that the 3D viewer renders only trajectory
// lines, making it ambiguous where the interceptor / target actually
// sit at launch and at CPA (closest-point-of-approach). The markers
// below are anchored to the start / end of each trajectory and use
// distinct shapes + colours so the eye can pick the vehicle pose at
// a glance even with the line subordinated. Shapes are Plotly
// scatter3d marker symbols: `square` and `diamond` are interceptor
// states; `circle-open` (start) and `x` (CPA) are target states.
// ──────────────────────────────────────────────────────────────────
export const VEHICLE_MARKER_STYLES = {
    interceptor_launch: {
        symbol: "square", color: "#38bdf8", size: 8,
        line: { color: "#0ea5e9", width: 2 },
        label: "Interceptor (launch)",
    },
    interceptor_cpa: {
        symbol: "diamond", color: "#22d3ee", size: 9,
        line: { color: "#0891b2", width: 2 },
        label: "Interceptor (CPA)",
    },
    target_start: {
        symbol: "circle-open", color: "#fbbf24", size: 8,
        line: { color: "#d97706", width: 2 },
        label: "Target (start)",
    },
    target_cpa: {
        symbol: "x", color: "#f59e0b", size: 10,
        line: { color: "#b45309", width: 2 },
        label: "Target (CPA)",
    },
};

/**
 * Build a list of scatter3d marker traces for a single trajectory
 * pair (interceptor + target). Each marker is its own trace so the
 * Plotly legend shows the 4 vehicle states inline.
 *
 *   interceptor / target = { x: [...], y: [...], z: [...] }
 *
 * Pass `showLegendOnly: true` for samples beyond the first so the
 * legend doesn't repeat entries — markers still render, but only
 * the first sample contributes legend rows.
 */
export function buildVehicleMarkers(interceptor, target, {
    showLegendOnly = false,
    sampleIdx = 0,
} = {}) {
    const out = [];
    const ix = interceptor?.x;
    const iy = interceptor?.y;
    const iz = interceptor?.z;
    const tx = target?.x;
    const ty = target?.y;
    const tz = target?.z;
    const has = (arr) => Array.isArray(arr) && arr.length > 0;

    function pushPoint(key, x, y, z) {
        const style = VEHICLE_MARKER_STYLES[key];
        out.push({
            type: "scatter3d",
            mode: "markers",
            name: style.label,
            x: [x], y: [y], z: [z],
            marker: {
                symbol: style.symbol,
                color: style.color,
                size: style.size,
                line: style.line,
            },
            showlegend: !showLegendOnly,
            legendgroup: key,
            hovertemplate: `${style.label} (sample ${sampleIdx})<extra></extra>`,
        });
    }

    if (has(ix) && has(iy) && has(iz)) {
        pushPoint("interceptor_launch", ix[0], iy[0], iz[0]);
        pushPoint(
            "interceptor_cpa",
            ix[ix.length - 1], iy[iy.length - 1], iz[iz.length - 1],
        );
    }
    if (has(tx) && has(ty) && has(tz)) {
        pushPoint("target_start", tx[0], ty[0], tz[0]);
        pushPoint(
            "target_cpa",
            tx[tx.length - 1], ty[ty.length - 1], tz[tz.length - 1],
        );
    }
    return out;
}

// ──────────────────────────────────────────────────────────────────
// Variable classifier — used by SensitivityDriver3D and its backend
// twin. Heuristic on `var_id` substrings; spatial axes get anchored
// glyphs, non-spatial drivers cluster in the side legend.
//
// Returns one of:
//   "altitude"   → glyph anchored along the z-axis at origin
//   "azimuth"    → glyph anchored along the ground-plane azimuth ring
//   "radial"     → glyph anchored along the East/range axis at z=0
//   "non_spatial"→ glyph clustered in the side stack (no axis anchor)
// ──────────────────────────────────────────────────────────────────
export function classifyDriverAxis(varId) {
    const id = String(varId ?? "").toLowerCase();
    if (id.includes("altitude") || id.includes("elev")) return "altitude";
    if (
        id.includes("heading") ||
        id.includes("bearing") ||
        id.includes("approach") ||
        id.includes("azimuth")
    ) {
        return "azimuth";
    }
    if (id.includes("range") || id.includes("standoff")) return "radial";
    return "non_spatial";
}
